import Lenis from 'lenis'

// Site-wide smooth/inertial scrolling, same engine used by react-bits'
// ScrollStack demo (github.com/DavidHDev/react-bits) — adopted for its
// scroll feel only, not the card-stacking/pinning behavior that component
// also does. Lifecycle is tied to Turbo page visits (init on turbo:load,
// destroy on turbo:before-cache) since it's a document-level effect rather
// than a per-component one, so it can't live inside react_mount's WeakMap
// mount guard.

let lenis = null
let rafId = null

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function raf(time) {
  lenis?.raf(time)
  rafId = requestAnimationFrame(raf)
}

export function initSmoothScroll() {
  destroySmoothScroll()
  if (prefersReducedMotion()) return

  lenis = new Lenis({
    duration: 1.1,
    easing: (t) => Math.min(1, 1 - Math.pow(2, -10 * t))
  })
  rafId = requestAnimationFrame(raf)

  // Arriving at a page with a section hash (e.g. from another page's nav
  // click) would otherwise get the browser's default instant jump before
  // Lenis exists. Reset to top and animate in instead, so it's consistent
  // with same-page nav clicks.
  const hash = window.location.hash
  if (hash && document.querySelector(hash)) {
    window.scrollTo(0, 0)
    settleThenScrollTo(hash)
  }
}

// The homepage keeps growing under the target section for a while after
// load — images streaming in, and the tagline's typing animation adding
// height as it reveals more text — so a single delayed scroll still lands
// short. Re-target on every layout change (debounced) until the page stops
// resizing, capped so it can't hang indefinitely.
function settleThenScrollTo(hash, { settleDelay = 200, maxWait = 3000 } = {}) {
  const start = performance.now()
  let debounceId = null

  const observer = new ResizeObserver(() => {
    clearTimeout(debounceId)
    debounceId = setTimeout(() => {
      lenis?.resize()
      scrollToSection(hash)
      if (performance.now() - start >= maxWait) observer.disconnect()
    }, settleDelay)
  })

  observer.observe(document.body)
  setTimeout(() => observer.disconnect(), maxWait)
}

export function destroySmoothScroll() {
  if (rafId) cancelAnimationFrame(rafId)
  rafId = null
  lenis?.destroy()
  lenis = null
}

// offset shifts the stop point up so a fixed header doesn't cover the
// section heading; matches the `scroll-mt-24` (6rem/96px) already on
// section elements for native/fallback scrolling.
export function scrollToSection(target, { offset = -96 } = {}) {
  const el = typeof target === 'string' ? document.querySelector(target) : target
  if (!el) return false

  if (lenis) {
    lenis.scrollTo(el, { offset, duration: 1.1 })
  } else {
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })
  }
  return true
}

export function scrollToTop() {
  if (lenis) {
    lenis.scrollTo(0, { duration: 1.1 })
  } else {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }
}
